/**
 * @module app.um
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { apiCall } from 'app/shared/lib/essette-api';
import { Toaster } from 'app/shared/lib/toaster';
import { AdmissionCareDayService } from 'app/um/services/admission-care-day-service';
import { deepClone } from 'app/shared/lib/utilities';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { CareDayStatusReasonReloadEvent } from 'app/um/lib/events';
import { I18N } from 'aurelia-i18n';
import * as UM from 'app/um/models/essette-api';

@autoinject()
export class SaveCareDayStatusReason extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.object({ defaultBindingMode: bindingMode.toView, changeHandler: 'reset' })
    public statusReason: UM.AdmissionCareDayStatusReason = {};

    private careDayStatusReasonReload: Subscription | null = null;

    // Value Bindings
    protected status: UM.AdmissionCareDayStatus | null = null;
    protected reason: string = '';
    protected code: string = '';

    // Drop Down/Select Options
    private statusOptions: UM.AdmissionCareDayStatus[] = [];

    protected maxReasonLength: number = 100;
    protected maxCodeLength: number = 50;

    ////****************** Lifecycle events (in order of execution) *****************////

    constructor(
        private readonly service: AdmissionCareDayService,
        private readonly toaster: Toaster,
        private readonly events: EventAggregator,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(bindingContext: Object, overrideContext: Object): void {
        this.reset();
    }

    protected attached(): void {
        this.careDayStatusReasonReload = this.events.subscribe(CareDayStatusReasonReloadEvent, (e: CareDayStatusReasonReloadEvent) => {
            this.reload();
        });
    }
    protected detached(): void {
        if(this.careDayStatusReasonReload) {
            this.careDayStatusReasonReload.dispose();
        }
    }

    ////****************** Events ***************************************************////
    protected async reload(): Promise<void> {
        this.loadLookups();
    }

    ////****************** Functions ************************************************////
    public async reset(): Promise<void> {
        this.reload();

        this.status = this.statusReason.status ? deepClone(this.statusReason.status) : null;
        this.reason = this.statusReason.description || '';
        this.code = this.statusReason.code || '';
    }

    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public async submit(): Promise<boolean> {
        return await this.saveStatusReason();
    }

    protected buildStatusReason(): UM.AdmissionCareDayStatusReason {
        return {
            id: this.statusReason.id,
            description: this.reason,
            code: this.code.trim().length ? this.code : null,
            status: {
                id: this.status?.id
            }
        };
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async saveStatusReason(): Promise<boolean> {
        let isSuccessful = false;
        let customErrorMessage;
        try {
            this.state.beginSave();
            const response = await this.service.saveCareDayStatusReason(this.buildStatusReason());
            isSuccessful = response.isSuccessful;
        }
        catch(error) {
            // If Duplicate Reason Code constraint violated, show toast
            if(error.status.errors) {
                const invalidCode = error.status.errors.find((err: any) => err.code === 'property.constraintViolated' && err.property === 'code');

                if(invalidCode) {
                    customErrorMessage = this.i18n.tr('ui:messages.duplicate.save_error', { what: this.i18n.tr('um:auth.careDays.status.reason.code.$long'), item: this.i18n.tr('um:auth.careDays.status.reason.$long') });
                }
            }
            throw error;
        }
        finally {
            if(customErrorMessage) {
                this.toaster.error().message(customErrorMessage).show();
            }
            else {
                this.toaster.standard.saved('um:auth.careDays.status.reason.$long', isSuccessful);
            }
            this.state.endSave();
            return isSuccessful;
        }
    }

    @apiCall
    private async loadLookups(): Promise<void> {
        try {
            this.state.beginLoad();

            const statuses = await this.service.loadCareDayStatuses({ all: true, includeInactive: this.statusReason && !!this.statusReason.id });
            this.statusOptions = statuses.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
